import { getTableColumnData } from 'src/components/collapsible-table/table-column-prep';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
import { useEffect, useState } from 'react';
import { Button, styled, Typography } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { useNavigate } from 'react-router-dom';
import { TableComponent as TableType } from '../pricing-simulator/types/generic-table.model';
import MODULE_TO_PAGE_MAP from './types/simulation-module-to-page-map';
import { Scenario } from '../scenarios/types/scenario.model';
import { setScenario } from './store/simulator-home-slice';
import ScenarioListDialog from '../scenario-list-dialog/scenario-list-dialog';
import { CreateNewScenarioDialog } from './components';
import ScenarioCreateSuccessDialog from './components/scenario-create-success/scenario-create-success-dialog';
import {
  getAccessConfigForScenario,
  getListOfScenariosForTable
} from './store/simulator-home-async-thunks';
import { fetchScenarioDetails } from './api/simulator-module-api';

const StyledButton = styled(Button)`
  background-color: #fcf1e8;
  border-radius: 10px;
  color: #ff6400;
`;

const getScenarioLandingRoute = (scenario: Scenario) => {
  return `/srm/simulator/${scenario.pk_id}/${
    (scenario?.planner_included ?? true) ? 'planner': 'innovation'
  }`;
}

export default function DuplicateScenario() {
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();

  const { sourceId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.PLANNER_HOME
  });

  const { scenarioList, isFetchingScenarios, inputColsForScenario } =
    useAppSelector((state) => state.simulatorHome);

  const [columns, setColumns] = useState([]);

  useEffect(() => {
    setColumns([
      ...getTableColumnData(inputColsForScenario, TableType.SCENARIOMANAGER, [])
    ]);
  }, [inputColsForScenario]);

  /*  Variable to list down all the scenarios to duplicate */
  const [isOpenOnDuplicateClick, setIsOpenOnDuplicateClick] = useState(false);
  /* Variable to open the create modal for duplicate scenarios */
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);

  const [selectedScenarioForDuplicate, setSelectedScenarioForDuplicate] =
    useState(null);

  const [isDuplicateSuccessDialogOpen, setIsDuplicateSuccessDialogOpen] =
    useState(false);

  /* Handle action on the click of a duplicate scenario. */
  const handleDuplicateScenario = () => {
    // get list of all the created scenarios
    appDispatch(
      getListOfScenariosForTable({
        sourceId
      })
    );
    setIsOpenOnDuplicateClick(true);
  };

  /* Handler to load the selected scenario for duplication */
  const handleLoadScenarioCloseForDuplicate = () => {
    setIsOpenOnDuplicateClick(false);
  };

  const onScenarioLoad = (scenarios: { scenario_id: number }[]) => {
    const [selectedRowDetails] = scenarios;
    if (selectedRowDetails) {
      return fetchScenarioDetails(sourceId, selectedRowDetails?.scenario_id)
        .then((response) => response.data)
        .then((scenario) => {
          setSelectedScenarioForDuplicate(scenario);
          setIsDuplicateDialogOpen(true);
        });
    }
    return Promise.resolve();
  };

  /* Handler to save the duplicate scenario. */
  const duplicateScenarioClickHandler = (scenario: Scenario) => {
    setIsDuplicateDialogOpen(false);
    if (scenario) {
      setIsDuplicateSuccessDialogOpen(true);
      appDispatch(setScenario(scenario));
      appDispatch(
        getAccessConfigForScenario({ scenarioId: scenario.pk_id, sourceId })
      ).then(() => {
        setTimeout(() => {
          navigate(getScenarioLandingRoute(scenario));
        }, 2000);
      });
    }
  };

  const handleDuplicateScenarioSuccessDialogClose = () => {
    setIsDuplicateSuccessDialogOpen(false);
  };

  return (
    <>
      <StyledButton
        sx={{ boxShadow: 3 }}
        variant='contained'
        onClick={handleDuplicateScenario}
      >
        <ContentCopyOutlinedIcon sx={{ fontSize: '2rem', margin: 2 }} />
      </StyledButton>
      <div>
        <Typography variant='text1'>Duplicate Saved Scenario</Typography>
      </div>
      {isOpenOnDuplicateClick ? (
        <ScenarioListDialog
          title='Duplicate Saved Scenario'
          fetchingTableData={isFetchingScenarios}
          open={isOpenOnDuplicateClick}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onClose={handleLoadScenarioCloseForDuplicate}
          tableRows={scenarioList.row}
          tableColumns={columns}
          footerBtnName='Duplicate'
          isMultiple={false}
          onScenarioLoad={onScenarioLoad}
        />
      ) : null}
      {isDuplicateDialogOpen ? (
        <CreateNewScenarioDialog
          isOpen={isDuplicateDialogOpen}
          isDuplicate
          isEditable={false}
          currentScenario={selectedScenarioForDuplicate}
          onClose={duplicateScenarioClickHandler}
        />
      ) : null}

      {isDuplicateSuccessDialogOpen ? (
        <ScenarioCreateSuccessDialog
          imagePath='/static/images/success.svg'
          isOpen={isDuplicateSuccessDialogOpen}
          onClose={handleDuplicateScenarioSuccessDialogClose}
          message='Scenario Duplicated Successfully'
        />
      ) : null}
    </>
  );
}
